// A small tokenizer for the languages component docs show: markup (html,
// Smarty, Razor), JS/TS/JSX, CSS, shell and diff. Output is escaped HTML with
// .edl-tok-* spans; anything it does not recognise passes through as text.

const esc = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]);
const span = (cls, s) => `<span class="edl-tok-${cls}">${esc(s)}</span>`;

const JS_KW = /^(?:import|from|export|default|const|let|var|function|return|if|else|for|while|switch|case|break|new|class|extends|async|await|typeof|instanceof|in|of|true|false|null|undefined|this|type|interface|as|satisfies)$/;

export function highlight(code, language = 'html') {
  const lang = String(language).toLowerCase();
  if (['html', 'xml', 'svg', 'markup', 'smarty', 'tpl', 'razor', 'cshtml', 'vue'].includes(lang)) return markup(code, lang);
  if (['js', 'javascript', 'jsx', 'ts', 'typescript', 'tsx', 'json', 'mjs'].includes(lang)) return script(code);
  if (['css', 'scss'].includes(lang)) return css(code);
  if (['sh', 'bash', 'shell', 'zsh'].includes(lang)) return shell(code);
  if (lang === 'diff') return diff(code);
  return esc(code);
}

// One HTML string per source line. A token that crosses a newline is closed
// at the break and reopened on the next line, so each row can render alone
// and keep its place while later lines stream in.
export function highlightToLines(code, language = 'html') {
  return splitHtmlLines(highlight(String(code), language));
}

function splitHtmlLines(html) {
  const lines = [];
  const open = [];
  let buf = '';
  let i = 0;
  const close = () => '</span>'.repeat(open.length);
  const reopen = () => open.join('');
  while (i < html.length) {
    if (html[i] === '\n') {
      lines.push(buf + close());
      buf = reopen();
      i += 1;
      continue;
    }
    if (html.startsWith('<span', i)) {
      const end = html.indexOf('>', i);
      if (end === -1) {
        buf += html.slice(i);
        break;
      }
      const tag = html.slice(i, end + 1);
      open.push(tag);
      buf += tag;
      i = end + 1;
      continue;
    }
    if (html.startsWith('</span>', i)) {
      open.pop();
      buf += '</span>';
      i += 7;
      continue;
    }
    let j = i + 1;
    while (j < html.length && html[j] !== '\n' && html[j] !== '<') j += 1;
    buf += html.slice(i, j);
    i = j;
  }
  lines.push(buf);
  return lines;
}

function markup(code, lang) {
  let out = '';
  let i = 0;
  const smarty = lang === 'smarty' || lang === 'tpl';
  while (i < code.length) {
    if (code.startsWith('<!--', i)) {
      const end = code.indexOf('-->', i + 4);
      const stop = end === -1 ? code.length : end + 3;
      out += span('com', code.slice(i, stop));
      i = stop;
    } else if (smarty && code.startsWith('{*', i)) {
      const end = code.indexOf('*}', i + 2);
      const stop = end === -1 ? code.length : end + 2;
      out += span('com', code.slice(i, stop));
      i = stop;
    } else if (smarty && code[i] === '{' && /[\w$/]/.test(code[i + 1] ?? '')) {
      const stop = matchBrace(code, i);
      out += smartyTag(code.slice(i, stop));
      i = stop;
    } else if (code[i] === '<' && /[a-zA-Z/!]/.test(code[i + 1] ?? '')) {
      const stop = tagEnd(code, i);
      const src = code.slice(i, stop);
      out += tag(src);
      i = stop;
      // The body of <style> and <script> is CSS and JS, not markup
      const embedded = src.match(/^<(style|script)\b/i)?.[1].toLowerCase();
      if (embedded && !src.endsWith('/>')) {
        const close = code.toLowerCase().indexOf(`</${embedded}`, i);
        const end = close === -1 ? code.length : close;
        out += embedded === 'style' ? css(code.slice(i, end)) : script(code.slice(i, end));
        i = end;
      }
    } else {
      const next = nextSpecial(code, i, smarty);
      out += esc(code.slice(i, next));
      i = next;
    }
  }
  return out;
}

function nextSpecial(code, from, smarty) {
  for (let j = from + 1; j < code.length; j++) {
    if (code[j] === '<' || (smarty && code[j] === '{')) return j;
  }
  return code.length;
}

function tagEnd(code, i) {
  let quote = null;
  for (let j = i + 1; j < code.length; j++) {
    const c = code[j];
    if (quote) {
      if (c === quote) quote = null;
    } else if (c === '"' || c === "'") quote = c;
    else if (c === '>') return j + 1;
  }
  return code.length;
}

function matchBrace(code, i) {
  let depth = 0;
  for (let j = i; j < code.length; j++) {
    if (code[j] === '{') depth++;
    else if (code[j] === '}' && --depth === 0) return j + 1;
  }
  return code.length;
}

function tag(src) {
  const m = src.match(/^(<\/?)([\w:.-]+|!\w+)([\s\S]*?)(\/?>)?$/);
  if (!m) return esc(src);
  const [, open, name, rest = '', close = ''] = m;
  let attrs = '';
  const re = /([^\s=]+)(\s*=\s*)("[^"]*"|'[^']*'|[^\s"'>]+)?|(\s+)/g;
  let last = 0;
  let a;
  while ((a = re.exec(rest))) {
    if (a.index > last) attrs += esc(rest.slice(last, a.index));
    if (a[4]) attrs += a[4];
    else {
      attrs += span('attr', a[1]);
      if (a[2]) attrs += span('punct', a[2]);
      if (a[3]) attrs += span('str', a[3]);
    }
    last = re.lastIndex;
  }
  if (last < rest.length) attrs += esc(rest.slice(last));
  return span('punct', open) + span('tag', name) + attrs + span('punct', close);
}

function smartyTag(src) {
  const inner = src.slice(1, -1);
  const m = inner.match(/^(\/?[\w$.]+)([\s\S]*)$/);
  if (!m) return span('smarty', src);
  let rest = '';
  const re = /("[^"]*"|'[^']*')|(\b[\w-]+)(?==)|(=>?)/g;
  let last = 0;
  let t;
  while ((t = re.exec(m[2]))) {
    if (t.index > last) rest += esc(m[2].slice(last, t.index));
    if (t[1]) rest += span('str', t[1]);
    else if (t[2]) rest += span('attr', t[2]);
    else rest += span('punct', t[3]);
    last = re.lastIndex;
  }
  if (last < m[2].length) rest += esc(m[2].slice(last));
  return span('punct', '{') + span('smarty', m[1]) + rest + span('punct', '}');
}

function script(code) {
  const re = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|(`(?:\\[\s\S]|[^`])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_$][\w$]*\b)|(<\/?[A-Z][\w.]*|<\/?[a-z][\w-]*(?=[\s>/]))/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(code))) {
    if (m.index > last) out += esc(code.slice(last, m.index));
    if (m[1]) out += span('com', m[1]);
    else if (m[2]) out += span('str', m[2]);
    else if (m[3]) out += span('num', m[3]);
    else if (m[4]) out += JS_KW.test(m[4]) ? span('kw', m[4]) : esc(m[4]);
    else if (m[5]) out += span('tag', m[5]);
    last = re.lastIndex;
  }
  return out + esc(code.slice(last));
}

function css(code) {
  const re = /(\/\*[\s\S]*?\*\/)|("[^"]*"|'[^']*')|(--[\w-]+|[\w-]+(?=\s*:))|(#[0-9a-fA-F]{3,8}\b|\b\d+(?:\.\d+)?(?:px|rem|em|%|s|ms)?\b)/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(code))) {
    if (m.index > last) out += esc(code.slice(last, m.index));
    if (m[1]) out += span('com', m[1]);
    else if (m[2]) out += span('str', m[2]);
    else if (m[3]) out += span('attr', m[3]);
    else out += span('num', m[4]);
    last = re.lastIndex;
  }
  return out + esc(code.slice(last));
}

function diff(code) {
  return code
    .split('\n')
    .map((line) => {
      if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@') || line.startsWith('diff ') || line.startsWith('index ')) return span('meta', line);
      if (line.startsWith('+')) return span('add', line);
      if (line.startsWith('-')) return span('del', line);
      return esc(line);
    })
    .join('\n');
}

function shell(code) {
  return code
    .split('\n')
    .map((line) => {
      if (line.trim().startsWith('#')) return span('com', line);
      const m = line.match(/^(\s*)(\S+)([\s\S]*)$/);
      return m ? m[1] + span('kw', m[2]) + esc(m[3]) : esc(line);
    })
    .join('\n');
}
